// web/src/lib/beamSynth.ts
// The tractor beam's VOICE — a Tone.js synth that charges up with the beam. Two detuned saws
// through a lowpass + tremolo + light reverb; `setBeamIntensity(charge)` (0–100) drives its
// pitch (rising ~2 octaves), brightness (filter opening), loudness, detune tension, and pulse
// rate, so it swells from a low hum to a bright, fast-throbbing energy peak as the charge climbs.
// Module singleton; a `gen` token cancels stale async starts (StrictMode / load race).
import * as Tone from "tone";

type BeamNodes = {
  osc1: Tone.Oscillator; osc2: Tone.Oscillator; filter: Tone.Filter;
  tremolo: Tone.Tremolo; gain: Tone.Gain; reverb: Tone.Reverb;
};
let beam: BeamNodes | null = null;
let gen = 0;

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

function disposeBeam(b: BeamNodes): void {
  try { b.osc1.dispose(); b.osc2.dispose(); b.filter.dispose(); b.tremolo.dispose(); b.gain.dispose(); b.reverb.dispose(); } catch { /* gone */ }
}

/** Start the beam voice (idempotent, silent until setBeamIntensity ramps it up). Needs a prior
 *  user gesture for the AudioContext (the Create "do it" click, or any /dev/loader interaction). */
export async function startBeamSynth(): Promise<void> {
  if (beam) return;
  const token = ++gen;
  try {
    await Tone.start();
    const reverb = new Tone.Reverb({ decay: 2.5, wet: 0.22 });
    const gain = new Tone.Gain(0); // amplitude — driven by intensity
    const tremolo = new Tone.Tremolo({ frequency: 2.5, depth: 0.45, spread: 0 }).start();
    const filter = new Tone.Filter({ type: "lowpass", frequency: 120, Q: 2 });
    const osc1 = new Tone.Oscillator({ type: "sawtooth", frequency: 48, volume: -8 });
    const osc2 = new Tone.Oscillator({ type: "sawtooth", frequency: 48, detune: 8, volume: -12 });
    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(tremolo);
    tremolo.connect(gain);
    gain.connect(reverb);
    reverb.toDestination();
    await reverb.ready;
    if (token !== gen) { osc1.dispose(); osc2.dispose(); filter.dispose(); tremolo.dispose(); gain.dispose(); reverb.dispose(); return; }
    beam = { osc1, osc2, filter, tremolo, gain, reverb };
    osc1.start();
    osc2.start();
  } catch {
    /* audio blocked / no context — skip the beam voice silently */
  }
}

/** Drive the beam voice from the charge (0–100): pitch, brightness, loudness, detune, pulse.
 *  Sits UNDER the music as a texture — its loudness peaks around mid-charge then EASES back, so
 *  the long Ignite hold recedes and the music's natural build stays the dominant climax rather
 *  than the beam blaring a constant tone through the wait. */
export function setBeamIntensity(charge: number): void {
  const b = beam;
  if (!b) return;
  const t = clamp01(charge / 100);
  // Loudness drive: rise to a peak by ~68% charge, then ease back toward the top so the beam
  // gets out of the music's way for the climax (and never holds at full through the Ignite wait).
  const drive = t < 0.68 ? t / 0.68 : 1 - ((t - 0.68) / 0.32) * 0.5;
  const bright = t * t;
  try {
    b.gain.gain.rampTo(0.006 + drive * 0.13, 0.18); // well under the music (~-22dB hold, ~-17dB peak)
    const freq = 46 * Math.pow(2, t * 1.9); // ~46Hz → ~172Hz rise (keeps "charging" even as it eases)
    b.osc1.frequency.rampTo(freq, 0.18);
    b.osc2.frequency.rampTo(freq, 0.18);
    b.osc2.detune.rampTo(6 + t * 14, 0.25); // some tension high up
    b.filter.frequency.rampTo(110 + bright * 2000, 0.2); // opens but caps lower → far less piercing
    b.tremolo.frequency.rampTo(2 + t * 5, 0.25); // gentler pulse (max ~7Hz), not a fast flutter
  } catch {
    /* signal not ready — ignore */
  }
}

/** Fade out + dispose the beam voice (cancel / transition / unmount). */
export function stopBeamSynth(): void {
  gen++; // cancel any in-flight start
  const b = beam;
  beam = null;
  if (!b) return;
  try {
    b.gain.gain.rampTo(0, 0.3);
    setTimeout(() => disposeBeam(b), 500);
  } catch {
    disposeBeam(b);
  }
}
